/**
 * MIN-292 — the React face of `./thread-conversation`: resolves a thread's
 * shared `Chat` and folds its persisted history into it once, so a surface
 * mounting mid-stream joins the conversation already in progress rather than
 * freezing an empty one.
 *
 * Takes auth/baseUrl/route-context explicitly rather than off an
 * `AgentChat.Provider` — the provider's client is the headless command face
 * (create/delete/list/read a thread), not a transport; a thread's `Chat`
 * needs the transport itself, which is a host concern (which auth object,
 * which engine URL, which route params exist) each app already supplies to
 * its query definitions today.
 */
import type { Chat } from "@ai-sdk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UIMessage } from "ai";
import { useEffect } from "react";
import type { ChatRouteContext } from "./chat-context";
import type { ChatAuth } from "./chat-fetch";
import { chatMessageQueries } from "./queries";
import type { ThreadId } from "./thread";
import { hydrateThreadConversation, resolveThreadConversation } from "./thread-conversation";

export interface UseThreadChatDeps {
  auth: ChatAuth;
  baseUrl: string;
  /** Read at send time, not build time — pass the same closure every render. */
  getRouteContext: () => ChatRouteContext;
}

export interface ThreadChatHandle {
  /** Shared with every other surface showing this thread. */
  chat: Chat<UIMessage>;
  /** The server's history hasn't arrived yet. */
  seedPending: boolean;
}

/**
 * Resolves a thread's shared `Chat` and folds the server's history into it
 * once. Hydration is idempotent across surfaces, so it does not matter which
 * mount wins the race — and a history refetch landing after a stream has
 * started can no longer overwrite it.
 */
export function useThreadChat(threadId: ThreadId, deps: UseThreadChatDeps): ThreadChatHandle {
  const { auth, baseUrl, getRouteContext } = deps;
  const queryClient = useQueryClient();
  const { data: seedMessages } = useQuery(chatMessageQueries.byThread(auth, baseUrl, threadId));

  const chat = resolveThreadConversation(threadId, { auth, baseUrl, queryClient, getRouteContext });

  useEffect(() => {
    if (seedMessages) hydrateThreadConversation(threadId, seedMessages);
  }, [threadId, seedMessages]);

  return { chat, seedPending: seedMessages === undefined };
}
